import React, { useState, useEffect } from 'react';
import { Plus, LayoutGrid, List, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import cuttingPlanService from '../services/cuttingPlanService';
import toast from 'react-hot-toast';
import CuttingPlanFilters from './CuttingPlanFilters';
import CuttingPlanTable from './CuttingPlanTable';
import CuttingPlanCard from './CuttingPlanCard';
import ConfirmModal from './ConfirmModal';

const CuttingPlansList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    fromDate: '',
    toDate: ''
  });
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [pendingDeletePlan, setPendingDeletePlan] = useState(null);

  const loadPlans = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        limit: 20,
        offset: (currentPage - 1) * 20
      };

      if (filters.search) {
        params.plan_code = filters.search;
      }
      if (filters.status) {
        params.status = filters.status;
      }
      if (filters.fromDate) {
        params.fromDate = filters.fromDate;
      }
      if (filters.toDate) {
        params.toDate = filters.toDate;
      }

      const response = await cuttingPlanService.getPlans(params);

      if (response.success && response.data) {
        setPlans(response.data);
        setTotalPages(Math.ceil((response.total || response.data.length) / 20) || 1);
      } else {
        setPlans([]);
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
      setError('Erro ao carregar planos de corte');
      toast.error('Erro ao carregar planos de corte');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, [currentPage, filters]);

  useEffect(() => {
    const handleFocus = () => {
      loadPlans();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [currentPage, filters]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleView = (plan) => {
    navigate(`/cutting-plans/${plan.id}`);
  };

  const handleEdit = (plan) => {
    navigate(`/cutting-plans/${plan.id}/edit`);
  };

  const handleDelete = (plan) => {
    setConfirmMessage(`Tem certeza que deseja excluir o plano ${plan.plan_code}?`);
    setPendingDeletePlan(plan);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeletePlan) return;

    try {
      await cuttingPlanService.deletePlan(pendingDeletePlan.id);
      toast.success('Plano excluído com sucesso');
      loadPlans();
    } catch (error) {
      console.error('Erro ao deletar plano:', error);
      toast.error('Erro ao deletar plano de corte');
    } finally {
      setIsConfirmModalOpen(false);
      setPendingDeletePlan(null);
    }
  };

  const handleCancelDelete = () => {
    setIsConfirmModalOpen(false);
    setPendingDeletePlan(null);
  };

  const handleCreate = () => {
    navigate('/cutting-plans/new');
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Planos de Corte</h1>
          <p className="text-gray-600 mt-1">
            Gerencie seus planos de corte
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Novo Plano
        </button>
      </div>

      <CuttingPlanFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />

      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600">
          {plans.length > 0 && (
            <span>
              {plans.length} plano(s) encontrado(s)
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('table')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'table'
                ? 'bg-white shadow text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <List className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'grid'
                ? 'bg-white shadow text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <LayoutGrid className="w-5 h-5" />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {viewMode === 'table' ? (
        <CuttingPlanTable
          plans={plans}
          loading={loading}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-full flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Carregando...</span>
            </div>
          ) : plans.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-500">Nenhum plano de corte encontrado</p>
            </div>
          ) : (
            plans.map((plan) => (
              <CuttingPlanCard
                key={plan.id}
                plan={plan}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        message={confirmMessage}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default CuttingPlansList;
